// @flow
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/styles';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Fade from '@material-ui/core/Fade';

import AvatarLogoIcon from '../Icons/AvatarLogo';
import CloseIcon from '../Icons/Close';
import OpenOrDownloadAvaAppComponent from '../OpenOrDownloadAvaAppComponent';

import RatingContent from './RatingContent';
import RatingExcellent from './RatingExcellent';
import RatingAverage from './RatingAverage';
import RatingPoor from './RatingPoor';

import {
  clearConversationStatus,
  setRate,
  setRateHover,
  setRateConversationOpen,
} from '../../actions/rate-conversation';
import { fetchAvaProfileMetrics } from '../../actions/session';
import { selectRate, selectRateHover } from '../../selectors/rate-conversation';
import { selectElectronCaptionMode } from '../../selectors/scribe-transcript-ui';

import { conversations } from '../../services/api/ava';

import { trackConversationRating } from '../../utils/conversation';
import { showNewMessage } from '../../utils/intercom';

const useStyles = makeStyles(theme => {
  const INITIAL_WIDTH = 432;
  const INITIAL_HEIGHT = 368;

  return {
    root: {
      position: 'fixed',
      right: 20,
      bottom: 20,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: INITIAL_WIDTH,
      height: INITIAL_HEIGHT,
      padding: '16px 48px 24px 48px',
      background: theme.palette.type === 'light' ? theme.palette.ava.darkBg1 : theme.palette.ava.white,
      boxShadow:
        theme.palette.type === 'light'
          ? '0px 4px 24px rgba(112, 144, 176, 0.25)'
          : '0px 4px 4px rgba(112, 144, 176, 0.04)',
      borderRadius: 20,
      color: theme.palette.type === 'light' ? theme.palette.ava.white : theme.palette.ava.dark1,
      textAlign: 'center',
      zIndex: 2147483002,
      [theme.breakpoints.down('sm')]: {
        width: INITIAL_WIDTH - 40,
        height: INITIAL_HEIGHT - 40,
      },
      [theme.breakpoints.down('xs')]: {
        right: 0,
        bottom: 0,
        width: '100%',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    closeButton: {
      position: 'absolute',
      backgroundColor: 'transparent',
      color: theme.palette.type === 'dark' ? 'white' : 'black',
      right: -12,
      top: -12,
      outline: 'none',
      border: 'none',
      borderRadius: 100,
      '&:hover': {
        background: 'transparent',
      },
    },
    avatarLogo: {
      marginTop: 12,
      marginBottom: 8,
      [theme.breakpoints.down('sm')]: {
        marginTop: 8,
        marginBottom: 4,
      },
    },
    avatarIcon: {
      width: 70,
      height: 70,
      [theme.breakpoints.down('sm')]: {
        width: 60,
        height: 60,
      },
    },
    content: {
      flex: 1,
    },
  };
});

const RateConversation = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const rate = useSelector(selectRate);
  const rateHover = useSelector(selectRateHover);
  const electronCaptionMode = useSelector(selectElectronCaptionMode);
  const hostName = useSelector(state => state.rateConversation.hostName);
  const conversationStatus = useSelector(state => state.rateConversation.conversationStatus);
  const transcriptUser = useSelector(state => state.scribeSession.captioner);
  const dispatch = useDispatch();

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [showOpenOrDownloadAvaApp, setShowOpenOrDownloadAvaApp] = useState(false);
  const [showRating, setShowRating] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setShowRating(true);
    }, 1500);
  }, []);

  const trackRateEvent = (rate: number) => {
    trackConversationRating({
      fetchAvaProfileMetrics: (updatedMetrics: Object) => dispatch(fetchAvaProfileMetrics(updatedMetrics)),
      rate,
      status: conversationStatus,
      user: transcriptUser,
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    const handleUnload = () => {
      trackRateEvent(0);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const submitRate = ({ rating }) => {
    if (rating !== undefined) {
      conversations.rateConversation({
        avaId: transcriptUser.avaId,
        roomId: conversationStatus.id,
        rating,
        firebaseAuthUID: transcriptUser.id,
      });
    }
  };

  const onClose = () => {
    setShowRating(false);
  };

  const submit = ({ rating }) => {
    if (!rating) {
      // Had conversation is automatically
      // tracked when user rates,
      // we need to call it when use closes popup
      trackRateEvent(0);
    }
    submitRate({ rating });
  };

  const onRateChange = (newRate: number) => {
    // Return if already rated
    if (rate) {
      return;
    }
    dispatch(setRate(newRate));

    // Track as soon as we rate
    trackRateEvent(newRate);

    // If no CTA, let's close
    const isOnDesktopAndLoggedIn = window.isElectron && transcriptUser.avaName;
    if ((newRate >= 4 && isOnDesktopAndLoggedIn) || newRate === 3) {
      setTimeout(() => {
        submit({ rating: newRate });
        onClose();
      }, 2000);
    } else {
      submit({ rating: newRate });
    }
  };

  const onRateHover = (value: number) => {
    if (rateHover !== value) {
      dispatch(setRateHover(value));
    }
  };

  const openOrDownloadAvaApp = () => {
    // Since it's based on rendering a component,
    // we toggle the visibility alltogether
    setShowOpenOrDownloadAvaApp(false);
    setTimeout(() => {
      setShowOpenOrDownloadAvaApp(true);
    }, 0);
  };

  if (electronCaptionMode) {
    return null;
  }

  return (
    <Fade
      in={showRating}
      timeout={{ enter: 700, exit: 500 }}
      onExited={() => {
        if (!rate) {
          trackRateEvent(0);
          dispatch(clearConversationStatus());
        }
        dispatch(setRate(undefined));
        dispatch(setRateHover(undefined));
        dispatch(setRateConversationOpen(false));
      }}
      unmountOnExit
    >
      <div className={classes.root}>
        <Button className={classes.closeButton} onClick={onClose}>
          <CloseIcon
            color={theme.palette.type === 'light' ? theme.palette.ava.white : theme.palette.ava.dark2}
            width={70}
            height={70}
          />
        </Button>

        <div className={classes.avatarLogo}>
          {transcriptUser && transcriptUser.userPhoto && transcriptUser.userPhoto.url ? (
            <Avatar className={classes.avatarIcon} src={transcriptUser.userPhoto.url} alt="profile_photo" />
          ) : (
            <AvatarLogoIcon
              bgTheme="light"
              color={theme.palette.ava.navy2}
              width={screenWidth < 900 ? 60 : 70}
              height={screenWidth < 900 ? 60 : 70}
            />
          )}
        </div>

        <div className={classes.content}>
          {rate === undefined && (
            <RatingContent rate={rate} rateHover={rateHover} onRateChange={onRateChange} onRateHover={onRateHover} />
          )}

          {rate !== undefined && rate >= 4 && (
            <RatingExcellent userExists={!!transcriptUser.avaName} hostName={hostName} onClick={openOrDownloadAvaApp} />
          )}

          {rate !== undefined && rate === 3 && (
            <RatingAverage userExists={!!transcriptUser.avaName} hostName={hostName} onClick={openOrDownloadAvaApp} />
          )}

          {rate !== undefined && rate <= 2 && (
            <RatingPoor
              onClick={() => {
                showNewMessage(t('conversation.over.iHadTheFollowingIssues'));
                onClose();
              }}
            />
          )}
        </div>

        {showOpenOrDownloadAvaApp && <OpenOrDownloadAvaAppComponent />}
      </div>
    </Fade>
  );
};

export default withRouter(RateConversation);
